import usePrevious from 'hooks/usePrevious';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, REMOVE_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { generateRandomId, produce } from 'utility/general';
import { cloneDeep, isEqual } from 'lodash';

const useStudyArticleDetailData = ({ formId = -1, siteInfoId = -1 }) => {
    const [openPopup, setOpenPopup] = useState(false);
    // const [selectedRow, setSelectedRow] = useState(null);
    const dispatch = useDispatch();
    const serverTableKey = 'studyArticleDetail';
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const [rows, setRows] = useState(remoteData ?? []);
    const [isEdit, setEdit] = useState(false);
    const previousData = usePrevious(remoteData);
    const [dialogData, setDialogData] = useState({});


    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const updateRemoteData = () => {
        setRows(remoteData);
    };

    const onDialogDataUpdate = (e, clearFields = []) => {
        const { value, name } = e.target;

        setDialogData(
            produce((draft) => {
                draft[name] = value;

                clearFields?.forEach((item) => {
                    delete draft[item];
                });
            }),
        );
    };

    const [choice] = useState({
        articleControl: [
            { key: 'A', label: 'Article' },
            { key: 'C', label: 'Control' },
        ],
        type: [
            { key: 0, label: 'Complementary medicine' },
            { key: 1, label: 'Dental' },
            { key: 2, label: 'Device' },
            { key: 3, label: 'Diet supplement' },
            { key: 4, label: 'Drug' },
            { key: 5, label: 'Exercise' },
            { key: 6, label: 'Health services' },
            { key: 7, label: 'Herb' },
            { key: 8, label: 'Nursing' },
            { key: 9, label: 'Physiotherapy' },
            { key: 10, label: 'Radiotherapy' },
            { key: 11, label: 'Surgery' },
            { key: 12, label: 'Vaccine' },
            { key: 13, label: 'Others' },
        ],
        routeAdmin: [
            { key: 0, label: 'Implant' },
            { key: 1, label: 'Inhalation' },
            { key: 2, label: 'Oral' },
            { key: 3, label: 'Parenteral' },
            { key: 4, label: 'Per rectal' },
            { key: 5, label: 'Topical' },
            { key: 6, label: 'Others' },
        ],
        underGMP: [
            { key: 0, label: 'Yes' },
            { key: 1, label: 'No' },
            { key: 2, label: 'N/A' },
        ],
    });

    const defaultFormList = {
        articleControl: {
            value: dialogData['articleControl'],
            label: 'Article / Control',
            name: 'articleControl',
            type: 'select',
            operators: choice['articleControl'],
            onChange: onDialogDataUpdate,
            validators: ['isRequired'],
        },
        type: {
            value: dialogData['type'],
            label: 'Type',
            name: 'type',
            type: 'select',
            operators: choice['type'],
            onChange: onDialogDataUpdate,
            validators: ['isRequired'],
        },
        name: {
            value: dialogData['name'],
            label: 'Name',
            name: 'name',
            type: 'input',
            onChange: onDialogDataUpdate,
            validators: ['isRequired'],
            maxLength: 255,
        },
        duration: {
            value: dialogData['duration'],
            label: 'Duration of Exposure',
            name: 'duration',
            type: 'input',
            onChange: onDialogDataUpdate,
            validators: ['isRequired'],
            maxLength: 255,
        },
        dosage: {
            value: dialogData['dosage'],
            label: 'Dosage',
            name: 'dosage',
            type: 'input',
            onChange: onDialogDataUpdate,
            validators: ['isRequired'],
            maxLength: 255,
        },
        routeAdmin: {
            value: dialogData['routeAdmin'],
            label: 'Route of administration',
            name: 'routeAdmin',
            type: 'select',
            operators: choice['routeAdmin'],
            onChange: onDialogDataUpdate,
            validators: ['isRequired'],
        },
        underGMP: {
            value: dialogData['underGMP'],
            label: 'Was it produced under GMP?',
            name: 'underGMP',
            type: 'select',
            operators: choice['underGMP'],
            onChange: onDialogDataUpdate,
            validators: ['isRequired'],
        },
        others: {
            value: dialogData['others'],
            label: 'Others, specify',
            name: 'others',
            type: 'input',
            onChange: onDialogDataUpdate,
            allowAction: dialogData['type'] === 13 || dialogData['routeAdmin'] === 6,
            validators: dialogData['type'] === 13 || dialogData['routeAdmin'] === 6 ? ['isRequired'] : [],
            maxLength: 255,
        },
    };

    const getChoiceLabel = (type, key) => {
        return (
            choice[type]?.find((item) => {
                return item.key === key;
            })?.label ?? 'undefined'
        );
    };

    const getLabelName = (key) => {
        return defaultFormList[key]?.label ?? 'undefined';
    };

    const handleDelete = async (row) => {
        const deleteIndex = rows.findIndex((item) => item.id === row.id);

        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });
        } else {
            dispatch({
                type: ADD_DELETE_REQUEST,
                payload: {
                    type: serverTableKey,
                    id: row.id,
                    siteInfoId,
                    applicationFormId: formId,
                },
            });
        }

        return deleteIndex;
    };

    const recoverDataCreation = async (item) => {
        // handle edited data recovery
        const updatedPostData = [];

        rows.forEach((item) => {
            const remoteDataObject = remoteData.find((remoteItem) => {
                return remoteItem.id === item.id;
            });

            if (remoteDataObject) {
                const isSame = Object.entries(item).every(([key, value]) => {
                    // remove exception
                    if (key === 'siteInfo' || key === 'applicationForm') {
                        return true;
                    }

                    return remoteDataObject[key] === value;
                });

                if (!isSame) {
                    updatedPostData.push(item);
                }
            }
        });

        const postData = rows.filter((row) => {
            const isDataCreation = row.id.toString().startsWith('temp_');

            if (!isDataCreation) {
                dispatch({
                    type: REMOVE_DELETE_REQUEST,
                    payload: {
                        type: serverTableKey,
                        id: row.id,
                        applicationFormId: formId,
                    },
                });
            }

            return isDataCreation;
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...postData, ...updatedPostData],
                applicationFormId: formId,
            },
        });
    };

    const onDataEmpty = async (item) => {
        remoteData?.forEach((row) => {
            dispatch({
                type: ADD_DELETE_REQUEST,
                payload: {
                    type: serverTableKey,
                    id: row.id,
                    siteInfoId,
                    applicationFormId: formId,
                },
            });
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [],
                applicationFormId: formId,
            },
        });
    };

    const handleEditClick = (row) => {
        // setSelectedRow(row);
        setDialogData(row);
        setOpenPopup(true);
        setEdit(true);
    };

    const handleRowCreate = async (id) => {
        setDialogData({});
        setOpenPopup(true);
        setEdit(false);
    };

    const onRecordUpdate = async () => {
        const data = dialogData;
        const returnData = cloneDeep(dialogData);
        data.id = `temp_${generateRandomId()}`;

        if (data.routeAdmin !== 6 && data.type !== 13) {
            delete data.others;
        }

        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });
        setOpenPopup(false);

        return returnData;
    };

    const onEditSubmit = () => {
        const row = dialogData;
        const returnData = cloneDeep(dialogData);
        let postData = updatePostData ?? [];

        if (row.routeAdmin !== 6 && row.type !== 13) {
            delete row.others;
        }

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        if (index === -1) {
            postData.push(row);
        } else {
            postData[index] = row;
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );

        setOpenPopup(false);

        const indexInExistRow = rows.findIndex((item) => item.id === row.id);

        return { index: indexInExistRow, returnData };
    };

    const columns = [
        {
            field: 'articleControl',
            headerName: getLabelName('articleControl'),
            width: 150,
            valueGetter: (params) => {
                return getChoiceLabel('articleControl', params.value);
            },
        },
        {
            field: 'type',
            headerName: getLabelName('type'),
            width: 100,
            valueGetter: (params) => {
                return getChoiceLabel('type', params.value);
            },
        },
        { field: 'name', headerName: getLabelName('name'), width: 100 },
        { field: 'duration', headerName: getLabelName('duration'), width: 150 },
        { field: 'dosage', headerName: getLabelName('dosage'), width: 150 },
        {
            field: 'routeAdmin',
            headerName: getLabelName('routeAdmin'),
            width: 100,
            valueGetter: (params) => {
                return getChoiceLabel('routeAdmin', params.value);
            },
        },
        {
            field: 'underGMP',
            headerName: getLabelName('underGMP'),
            width: 100,
            valueGetter: (params) => {
                return getChoiceLabel('underGMP', params.value);
            },
        },
        { field: 'others', headerName: getLabelName('others'), width: 150 },
    ];

    return {
        columns,
        rows,
        setRows,
        openPopup,
        setOpenPopup,
        defaultFormList,
        isEdit,
        handleRowCreate,
        onRecordUpdate,
        handleEditClick,
        handleDelete,
        onEditSubmit,
        onDataEmpty,
        recoverDataCreation,
        updateRemoteData,
    };
};

export default useStudyArticleDetailData;
